import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import {
  TabContent,
  TabPane,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  CustomInput,
} from 'reactstrap';
import {
  ContAffiliation,
  H5Styled,
  ContFilterYear,
  BtnOutlineOrange,
  BtnOutlineGreen,
  ContPaginationPage,
} from '../../../../styled-components';
import SearchInput from '../../../../commons/SearchInput';
import generateMonthOptions from '../../../../lib/helpers/generateMonthOptions';
import EnergyRoyaltyTable from '../EnergyRoyaltyTable';
import TotalEnergyRevenuesRow from '../TotalTrEnergyRevenue';
import generateYearOptions from '../../../../lib/helpers/generateYearOptions';
import { useAuth } from '../../../../commons/Auth';
import variables from '../../../../variables';
import ReportingModal from '../../../../commons/ReportingModal';
import generatePaginationOptions from '../../../../lib/helpers/generatePaginationOptions';
import { USER_ADMIN_TYPES } from '../../../../App';
import EnergyRoyaltyTableBackoffice from '../EnergyRoyaltyTableBackoffice';
import moment from 'moment';

const VarRevEnergyDetailPresentational = ({
  data = {},
  profile,
  total,
  query,
  onFilter,
  onAddCommercialBonus,
}) => {
  const [t] = useTranslation('CONTACTS');
  const [{ newIsAdmin, newUserType = '' }] = useAuth();
  const { categories = {} } = data;
  const { variableRevenuesEnergy = {} } = categories;
  const [amount, setAmount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const alert = useAlert();
  const { data: dataRevEnergy = [] } = variableRevenuesEnergy;

  const origin = window.location.origin;
  let hash = window.location.hash.split('/');
  hash.pop();
  const prevPath =
    origin +
    '/' +
    hash.join('/') +
    `${query.year ? '?year=' + query.year : ''}`;

  const getVariableRevenuesEnergyTotals = (dataRev) => {
    const cashedTotals = dataRev.reduce((prev, cur) => {
      const { payments } = cur || {};
      return (
        prev +
        (payments || []).reduce((acc, payment) => {
          return acc + ((payment && payment.gross) || 0);
        }, 0)
      );
    }, 0);
    const netTotals = dataRev.reduce((prev, cur) => prev + cur?.net, 0);
    const grossTotals = dataRev.reduce((prev, cur) => prev + cur?.gross, 0);
    const commercialShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.commercialShare,
      0
    );
    const earnedCommercialShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.earnedCommercialShare,
      0
    );
    const billableCommercialShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.billableCommercialShare,
      0
    );
    return {
      cashedTotals,
      netTotals,
      grossTotals,
      commercialShareTotals,
      earnedCommercialShareTotals,
      billableCommercialShareTotals,
    };
  };
  const toggle = ({ selectAll: toggleSelectAll = false } = {}) => {
    if (isOpen) {
      setSelectAll(false);
      return setIsOpen(false);
    }

    setSelectAll(toggleSelectAll);

    const validRows = dataRevEnergy.map((row, index) => {
      if (row.checked || toggleSelectAll) {
        return {
          ...row,
          checked:
            row.billableCommercialShare -
              row?.iafs?.reduce((acc, iaf) => acc + iaf.amount, 0) >
            0,
        };
      }
      return row;
    });

    const checkedRows = validRows.filter(({ checked }) => checked);

    if (checkedRows.length) {
      const billableTotal = checkedRows.reduce(
        (acc, { billableCommercialShare = 0 }) => acc + billableCommercialShare,
        0
      );
      const alreadyBilled = checkedRows.reduce((acc, { iafs = [] }) => {
        return (
          acc +
            iafs.reduce((acc, { amount = 0 }) => {
              return acc + amount || 0;
            }, 0) || 0
        );
      }, 0);
      setAmount(+(billableTotal - alreadyBilled).toFixed(2));
      setIsOpen(!isOpen);
    } else {
      const message = toggleSelectAll
        ? t('ROYALTY:NO_ROW_IAF_SELECTABLE')
        : t('ROYALTY:SELECT_AT_LEAST_ONE');
      alert.error(message);
    }
  };

  const onSubmit = (formData) => {
    const validRows = dataRevEnergy.map((row, index) => {
      if (row.checked || selectAll) {
        return {
          ...row,
          checked:
            row.billableCommercialShare -
              row?.iafs?.reduce((acc, iaf) => acc + iaf.amount, 0) >
            0,
        };
      }
      return row;
    });

    const checkedRows = validRows.filter(({ checked }) => checked);

    const signingDateYear = moment(
      checkedRows[0]?.contract_signing_date
    ).year();
    for (const row of checkedRows) {
      const rowYear = moment(row.contract_signing_date).year();
      if (rowYear !== signingDateYear) {
        alert.error(
          'I contratti selezionati presentano anni di creazione differenti.'
        );
        toggle();
        return;
      }
    }

    const data = {
      ...formData,
      accountability: signingDateYear,
      iafType: 'ENERGY',
      contracts: checkedRows.map(
        ({
          _id,
          admin,
          condo,
          earnedCommercialShare,
          commercialShare,
          net,
          date,
          number,
          contract_signing_date,
        }) => ({
          _id,
          admin,
          condo,
          earnedCommercialShare,
          commercialShare,
          net,
          date,
          number,
          contract_signing_date: new Date(contract_signing_date),
        })
      ),
      amount,
    };
    onAddCommercialBonus(data);
    toggle();
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <TabContent activeTab={'1'}>
        <TabPane tabId="1">
          <ContFilterYear>
            <Row>
              <Col xs="6">
                <Label>{t('TITLE_FILTER_YEAR')}</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="selectYear"
                    name="year"
                    value={query && query.year}
                    onChange={({ target: { value: year } }) =>
                      onFilter({ year })
                    }
                  >
                    {generateYearOptions()}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
          <div
            style={{
              fontWeight: 700,
              color: '#333333',
              marginBottom: '20px',
              fontSize: '20px',
            }}
          >
            <a
              style={{
                color: variables.primary,
              }}
              href={prevPath}
            >
              Rendicontazioni network
            </a>{' '}
            / <a>{t('ENERGY:ROYALTIES_TITLE')}</a>
          </div>
          <ContAffiliation key={2}>
            <Row>
              <Col xs="6">
                <H5Styled>{t('ENERGY:ROYALTIES_TITLE')}</H5Styled>
              </Col>
              <Col xs="6" className="text-right">
                {newIsAdmin && (
                  <BtnOutlineGreen
                    type="button"
                    onClick={toggle}
                    style={{ marginRight: '16px' }}
                  >
                    {t('ROYALTY:INVITE_TO_INVOICE')}
                  </BtnOutlineGreen>
                )}
                {newIsAdmin && (
                  <BtnOutlineGreen
                    type="button"
                    onClick={() => toggle({ selectAll: true })}
                    style={{ marginRight: '16px' }}
                  >
                    {t('ROYALTY:INVITE_TO_INVOICE_ALL')}
                  </BtnOutlineGreen>
                )}
                {/*<CSVEnergyRevenuesExport*/}
                {/*  data={dataRevEnergy}*/}
                {/*  year={query && query.year}*/}
                {/*  businessName={profile ? profile.businessName : ''}*/}
                {/*>*/}
                {/*  <BtnOutlineOrange type="button" className="uppercase">*/}
                {/*    {t('COMMON:CSV_EXPORT')}*/}
                {/*  </BtnOutlineOrange>*/}
                {/*</CSVEnergyRevenuesExport>*/}
              </Col>
            </Row>
            <br />
            <Row className="compensi-variabili-resp">
              <Col sm="6" md="6">
                <SearchInput
                  query={{ search: query && query.energyPod }}
                  onFilter={({ search }) => onFilter({ energyPod: search })}
                  label={t('SEARCH:POD_PDR')}
                />
              </Col>
              <Col sm="6" md="6">
                <SearchInput
                  query={{ search: query && query.energyCondoVat }}
                  onFilter={({ search }) =>
                    onFilter({ energyCondoVat: search })
                  }
                  label={t('SEARCH:CONDO_VAT_CODE')}
                  autoFocus={false}
                />
              </Col>
              <Col sm="6" md="6">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>{t('COMMON:TITLE_FILTER_ACC_YEAR')}</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="selectEnergyAccountabilityYear"
                          name="energyAccountabilityYear"
                          value={query && query.energyAccountabilityYear}
                          onChange={({
                            target: { value: energyAccountabilityYear },
                          }) => {
                            onFilter({ energyAccountabilityYear });
                          }}
                        >
                          {generateYearOptions(true)}
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
              <Col sm="6" md="6">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>{t('COMMON:TITLE_FILTER_ACC_MONTH')}</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="energyAccountabilityMonth"
                          name="energyAccountabilityMonth"
                          disabled={!query.energyAccountabilityYear}
                          value={
                            query && query.energyAccountabilityMonth === null
                              ? ''
                              : query.energyAccountabilityMonth
                          }
                          onChange={({
                            target: { value: energyAccountabilityMonth },
                          }) => onFilter({ energyAccountabilityMonth })}
                        >
                          {generateMonthOptions()}
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
              <Col sm="12" md="4" lg="3" xl="3" className="select-wrapper">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>Tipo cliente</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="customerType"
                          name="customerType"
                          value={query && query.customerType}
                          onChange={({ target: { value: customerType } }) =>
                            onFilter({ customerType })
                          }
                        >
                          <option value="" defaultChecked>
                            Tutti
                          </option>
                          <option value="condos">Condomini</option>
                          <option value="customerExtra">Clienti privati</option>
                          <option value="utilizers">Utilizzatori</option>
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
            </Row>
            <br />
            {(newUserType === USER_ADMIN_TYPES.SUPER_ADMIN && (
              <EnergyRoyaltyTable
                data={dataRevEnergy || []}
                total={total}
                query={query}
                onFilter={onFilter}
                showEmptyTableMessage={true}
                bottomRow={
                  <TotalEnergyRevenuesRow
                    totals={getVariableRevenuesEnergyTotals(dataRevEnergy)}
                    route="variable-revenues-energy"
                    activeRoute={false}
                  />
                }
              />
            )) || (
              <EnergyRoyaltyTableBackoffice
                data={dataRevEnergy || []}
                total={total}
                query={query}
                onFilter={onFilter}
                showEmptyTableMessage={true}
                bottomRow={
                  <TotalEnergyRevenuesRow
                    totals={getVariableRevenuesEnergyTotals(dataRevEnergy)}
                    route="variable-revenues-energy"
                    activeRoute={false}
                  />
                }
              />
            )}

            <ContPaginationPage>
              <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

              <FormGroup>
                <CustomInput
                  type="select"
                  className="pagination-select"
                  id="perPage"
                  name="perPage"
                  value={query.limit}
                  onChange={({ target: { value: limit } }) => {
                    onFilter({ limit });
                  }}
                >
                  {generatePaginationOptions()}
                </CustomInput>
              </FormGroup>
            </ContPaginationPage>
          </ContAffiliation>
        </TabPane>
      </TabContent>

      <ReportingModal
        toggle={toggle}
        isOpen={isOpen}
        onSubmit={onSubmit}
        amount={amount}
        query={query}
      />
    </Form>
  );
};

export default VarRevEnergyDetailPresentational;
